import { useApiQuery } from 'hooks/useApiQuery';
import { apiSettlementsAgreementTemplates } from 'pages/SettingsPage/sections/SettlementsAgreementTemplates/api/apiSettlementsAgreementTemplates';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router';
import {
  apiSettlementSingle,
  EmailNotification,
  emptyUserList,
  SingleSettlement,
  UserList,
  UserOptionItem,
} from '../api/apiSettlementSingle';
import { validateSingleSettlement } from './settlementSingleContextUtils';
import { useSettlementAgreement } from './useSettlementAgreement';

type Dialog = {
  onClose: (confirmed?: boolean) => void;
};

interface SettlementSingleData {
  id: number;
  claimsField: string;
  loading: boolean;
  isDataUpdating: boolean;
  data: SingleSettlement | undefined;
  userList: UserList;
  isAllFieldsSet: boolean;
  agreementDocument: ReturnType<typeof useSettlementAgreement>;
  template: { name: string; exists: boolean };

  dialogSaveSuccessful: Dialog | undefined;
  dialogEmailNotification: Dialog | undefined;

  getSelectedCounterparty: () => UserOptionItem | undefined;
  setFieldValue: (
    field: keyof SingleSettlement,
  ) => (value: SingleSettlement[keyof SingleSettlement]) => void;
  clickButtonSave: () => void;
  clickButtonEmailNotification: (email?: EmailNotification) => void;
}

const SettlementSingleContext = createContext<SettlementSingleData>(
  {} as SettlementSingleData,
);

export const SettlementSingleContextProvider = (props: PropsWithChildren<{}>) => {
  const id = Number(useParams().id) || 0;
  const [initialData, setInitialData] = useState<SingleSettlement>();

  const [isAllFieldsSet, setIsAllFieldsSet] = useState(false);
  const [isDataUpdating, setIsDataUpdating] = useState(false);

  const [dialogSaveSuccessful, setDialogSaveSuccessful] = useState<Dialog>();
  const [dialogEmailNotification, setDialogEmailNotification] = useState<Dialog>();

  const settlementItem = useApiQuery({
    queryFn: () => apiSettlementSingle.getData(id),
  });

  const userList = useApiQuery({
    queryFn: () => apiSettlementSingle.getUsersList(),
  });

  const agreementTemplate = useApiQuery({
    queryFn: () => apiSettlementsAgreementTemplates.getTemplate(),
  });

  const data = settlementItem.data;

  useEffect(() => {
    if (settlementItem.loading || !settlementItem.data) return;
    setInitialData({ ...settlementItem.data });
  }, [settlementItem.loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const agreementDocument = useSettlementAgreement({
    id,
    fileName: data?.agreementFileName,
    onGenerateNew: () => settlementItem.fetch(),
  });

  const revalidateData = useCallback(() => {
    const validation = validateSingleSettlement(data);
    setIsAllFieldsSet(validation.valid);
  }, [data]);

  const claimsField = useMemo(
    () => data?.claims.map((r) => r.name).join(', ') ?? '',
    [data?.claims],
  );

  const setFieldValue = useCallback(
    (field: keyof SingleSettlement) =>
      (value: SingleSettlement[keyof SingleSettlement]): void => {
        if (!data) return;
        const newData: SingleSettlement = { ...data, [field]: value };
        settlementItem.setData(newData);

        revalidateData();
      },
    [data, settlementItem, revalidateData],
  );

  const template = useMemo(() => {
    const { data, loading } = agreementTemplate;
    return {
      name: data?.displayName ?? '',
      exists: !!data || loading,
    };
  }, [agreementTemplate]);

  const push = useCallback(
    async (email?: EmailNotification) => {
      setIsDataUpdating(true);
      const isSuccess = await apiSettlementSingle.update(data, email);
      setIsDataUpdating(false);

      if (!isSuccess) return;

      setDialogSaveSuccessful({
        onClose: () => setDialogSaveSuccessful(undefined),
      });
    },
    [data],
  );

  const clickButtonEmailNotification = useCallback(
    async (email?: EmailNotification) => {
      setDialogEmailNotification(undefined);
      if (!email?.send) setFieldValue('status')(initialData?.status);

      await push(email);
    },
    [initialData?.status, push, setFieldValue],
  );

  const clickButtonSave = useCallback(async () => {
    // if (isEmailNotification(initialData?.status, data?.status)) {
    //   setDialogEmailNotification({
    //     onClose: () => setDialogEmailNotification(undefined),
    //   });
    //   return;
    // }

    await push();
  }, [push]);

  const getSelectedCounterparty = useCallback(() => {
    const user = userList.data?.counterparty.find(
      (u) => u.value === data?.counterpartyId,
    );
    return user;
  }, [data?.counterpartyId, userList.data?.counterparty]);

  return (
    <SettlementSingleContext.Provider
      value={{
        id,
        claimsField,
        data,
        userList: userList.data ?? emptyUserList,
        loading: settlementItem.loading || userList.loading || isDataUpdating,
        isDataUpdating,
        isAllFieldsSet,
        dialogSaveSuccessful,
        dialogEmailNotification,
        agreementDocument,
        template,
        getSelectedCounterparty,
        setFieldValue,
        clickButtonEmailNotification,
        clickButtonSave,
      }}
    >
      {props.children}
    </SettlementSingleContext.Provider>
  );
};

export const useSettlementSingleContext = () => useContext(SettlementSingleContext);

// const isEmailNotification = (
//   initialStatus: SettlementStatus | undefined,
//   status: SettlementStatus | undefined,
// ) => {
//   const preparingStatuses: SettlementStatus[] = [
//     'SettlementPending',
//     'AwaitingClientApproval',
//   ];

//   const isPreparingState = preparingStatuses.includes(initialStatus!);
//   const isCounterpartyReviewAwaited = status === 'AwaitingCounterpartyReview';

//   return isPreparingState && isCounterpartyReviewAwaited;
// };
